import { components as c } from "@/components";
import { type TrackingType } from "@patient-watch/types";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@patient-watch/ui/form";
import { Button } from "@patient-watch/ui/button";
import { RadioCardGroup, RadioCardItem } from "@patient-watch/ui/radio-card-group";
import { EVENT_TYPES_WITH_SKIP } from "@/features/events/definitions/event-types";
import { insertEvent } from "@/features/events/api/insertEvent";
import { updateDiaryFlowSearchSchema } from "@/features/patients/update/updateDiaryFlowSearch";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";

type EventSelection = TrackingType | "skip";

type InterventionDetailsTo =
    | "/patients/$diaryId/update/$eventId/$trackerId/intervention-details/injection"
    | "/patients/$diaryId/update/$eventId/$trackerId/intervention-details/surgery"
    | "/patients/$diaryId/update/$eventId/$trackerId/intervention-details/shockwave"
    | "/patients/$diaryId/update/$eventId/$trackerId/intervention-details/education"
    | "/patients/$diaryId/update/$eventId/$trackerId/intervention-details/product";

const INTERVENTION_DETAIL_TYPES = new Set<string>(["injection", "surgery", "shockwave", "education", "product"]);

function getDetailsPath(eventType: TrackingType): InterventionDetailsTo {
    switch (eventType) {
        case "injection":
            return "/patients/$diaryId/update/$eventId/$trackerId/intervention-details/injection";
        case "surgery":
            return "/patients/$diaryId/update/$eventId/$trackerId/intervention-details/surgery";
        case "shockwave":
            return "/patients/$diaryId/update/$eventId/$trackerId/intervention-details/shockwave";
        case "education":
            return "/patients/$diaryId/update/$eventId/$trackerId/intervention-details/education";
        case "product":
            return "/patients/$diaryId/update/$eventId/$trackerId/intervention-details/product";
        default:
            return "/patients/$diaryId/update/$eventId/$trackerId/intervention-details/injection";
    }
}

const AddInterventionPage = () => {
    const copy = usePatientsCopy();
    const { diaryId, eventId, trackerId } = Route.useParams();
    const search = Route.useSearch();
    const { type } = search;
    const navigate = useNavigate();
    const form = useForm<{ event: EventSelection }>({
        defaultValues: { event: undefined },
    });
    const selectedEvent = form.watch("event");

    const required = search.required_events;
    const templateSingleEvent: TrackingType | null =
        Array.isArray(required) &&
        required.length === 1 &&
        required[0] &&
        INTERVENTION_DETAIL_TYPES.has(required[0] as string)
            ? (required[0] as TrackingType)
            : null;

    const [useManualIntervention, setUseManualIntervention] = useState(() => !templateSingleEvent);

    useEffect(() => {
        if (!templateSingleEvent || useManualIntervention) return;
        let cancelled = false;
        void (async () => {
            try {
                await insertEvent({ event_id: eventId, diary_id: diaryId });
                if (cancelled) return;
                const detailsPath = getDetailsPath(templateSingleEvent);
                void navigate({
                    to: detailsPath,
                    params: { diaryId, eventId, trackerId },
                    search: { ...search, event_type: templateSingleEvent } as never,
                });
            } catch {
                if (!cancelled) setUseManualIntervention(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [diaryId, eventId, navigate, templateSingleEvent, trackerId, useManualIntervention]);

    async function onSubmit(data: { event: EventSelection }) {
        const eventType = data.event;

        if (eventType === "skip") {
            await insertEvent({ event_id: eventId, diary_id: diaryId });
            void navigate({
                to: "/patients/$diaryId/update/$eventId/$trackerId/date",
                params: { diaryId, eventId, trackerId },
                search: { ...search, type },
            });
            return;
        }

        const detailsPath = getDetailsPath(eventType as TrackingType);
        void navigate({
            to: detailsPath,
            params: { diaryId, eventId, trackerId },
            search: { ...search, type, event_type: eventType } as never,
        });
    }

    if (templateSingleEvent && !useManualIntervention) {
        return (
            <div className="space-y-8 max-w-3xl">
                <c.cc.Title words={copy.intervention} description={copy.openingTemplateProcedure} />
            </div>
        );
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <c.cc.Title words={copy.intervention} description={copy.interventionTypeQuestion} />
                <FormField
                    control={form.control}
                    name="event"
                    rules={{
                        required: copy.selectInterventionRequired,
                    }}
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <RadioCardGroup value={field.value || ""} onValueChange={(val) => field.onChange(val)}>
                                    {EVENT_TYPES_WITH_SKIP.map((event) => (
                                        <RadioCardItem key={event.value} value={event.value} label={event.label} />
                                    ))}
                                </RadioCardGroup>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <Button size="lg" className="w-full" disabled={!selectedEvent} type="submit">
                    {copy.continueLabel}
                </Button>
            </form>
        </Form>
    );
};

export const Route = createFileRoute("/_app/patients/$diaryId/update/$eventId/$trackerId/intervention")({
    component: AddInterventionPage,
    validateSearch: updateDiaryFlowSearchSchema,
});
